import type { Answer, AssessmentResult, AQResult } from '@/types/assessment'
import { clinicIdentity } from '@/lib/clinic-identity'

type AssessmentId = 'asrs' | 'aq'
type RiskLevel = 'low' | 'moderate' | 'high'

type ScorerConfig = {
  id: AssessmentId
  numQuestions: number
  maxPerQuestion: number // e.g. ASRS=4, AQ=1
  itemScore: (value: number, qid: number) => number // normalize per-item score
  groups?: Record<string, number[]> // group name -> question ids
  thresholds: Array<{
    level: RiskLevel
    when: (ctx: {
      total: number
      pct?: number
      groups: Record<string, number>
    }) => boolean
    recommendations: (ctx: {
      total: number
      pct?: number
      groups: Record<string, number>
    }) => string[]
  }>
}

const phonesText = `${clinicIdentity.contact.landline.display} or ${clinicIdentity.contact.phone.display}`

/* ── AQ reverse-scored ids ──────────────────────────────────────────────── */
const reverseAQ = new Set([
  1, 3, 8, 10, 11, 14, 15, 17, 24, 25, 27, 28, 29, 30, 31, 32, 34, 36, 37, 38,
  40, 44, 47, 48, 49, 50,
])

/* ── Scoring configs (rules only; engine below is generic) ─────────────── */
const CONFIGS: Record<AssessmentId, ScorerConfig> = {
  asrs: {
    id: 'asrs',
    numQuestions: 18,
    maxPerQuestion: 4,
    itemScore: (v) => v ?? 0, // ASRS uses raw 0–4 values
    groups: {
      partA: [1, 2, 3, 4, 5, 6],
      partB: [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
    },
    thresholds: [
      {
        level: 'low',
        when: ({ pct }) => (pct ?? 0) < 30,
        recommendations: () => [
          `Your responses suggest a low likelihood of ADHD symptoms`,
          `Continue maintaining healthy habits and routines`,
          `Monitor any changes in attention or focus patterns`,
          `Consider lifestyle factors that support concentration`,
          `For professional consultation, call our clinic at ${phonesText}`,
        ],
      },
      {
        level: 'moderate',
        when: ({ pct }) => (pct ?? 0) < 60,
        recommendations: () => [
          `Your responses suggest some ADHD symptoms may be present`,
          `Consider discussing these results with a healthcare professional`,
          `Call our clinic at ${phonesText} for professional evaluation`,
          `Implement organizational strategies and time management techniques`,
          `Monitor symptoms and their impact on daily functioning`,
          `Consider stress management and sleep hygiene practices`,
        ],
      },
      {
        level: 'high',
        when: () => true,
        recommendations: () => [
          `Your responses suggest significant ADHD symptoms`,
          `Strongly consider seeking professional evaluation - call ${phonesText} for assessment`,
          `A mental health professional can provide proper diagnosis and treatment options`,
          `Document specific examples of symptoms for your healthcare provider`,
          `Consider immediate strategies for managing attention and hyperactivity`,
        ],
      },
    ],
  },

  aq: {
    id: 'aq',
    numQuestions: 50,
    maxPerQuestion: 1,
    // AQ collapses 4-point Likert to 0/1, with reverse scoring for some items
    itemScore: (v, qid) => {
      if (reverseAQ.has(qid)) return v === 3 || v === 4 ? 1 : 0 // Disagree => 1
      return v === 1 || v === 2 ? 1 : 0 // Agree    => 1
    },
    groups: {
      social: [
        1, 7, 10, 11, 13, 15, 17, 20, 22, 24, 31, 36, 38, 44, 45, 47, 48,
      ],
      communication: [18, 26, 27, 33, 35, 39],
      imagination: [3, 8, 14, 21, 40, 42, 50],
      attention: [4, 5, 6, 9, 12, 19, 23, 28, 29, 30, 32, 37, 41, 49],
      routine: [2, 16, 25, 34, 43, 46],
    },
    thresholds: [
      {
        level: 'low',
        when: ({ total }) => total < 26,
        recommendations: () => [
          `Your responses suggest a low likelihood of autism spectrum traits`,
          `Continue developing your social and communication skills`,
          `Maintain awareness of neurodiversity and different ways of thinking`,
          `For any concerns, call our clinic at ${phonesText}`,
        ],
      },
      {
        level: 'moderate',
        when: ({ total }) => total < 32,
        recommendations: () => [
          `Your responses suggest some autism spectrum traits may be present`,
          `Consider discussing these results with a healthcare professional`,
          `Call our clinic at ${phonesText} for professional evaluation`,
          `Explore strategies for social communication and sensory management`,
          `Consider connecting with autism support communities`,
          `Document specific examples of traits for your healthcare provider`,
        ],
      },
      {
        level: 'high',
        when: () => true,
        recommendations: () => [
          `Your responses suggest significant autism spectrum traits`,
          `Strongly consider seeking professional evaluation - call ${phonesText} for comprehensive assessment`,
          `A qualified professional can provide proper diagnosis and support recommendations`,
          `Consider immediate strategies for managing sensory sensitivities and social situations`,
          `Connect with autism support groups and resources in your area`,
        ],
      },
    ],
  },
}

/* ── Generic engine ─────────────────────────────────────────────────────── */
export type GenericResult = {
  totalScore: number
  percentageScore?: number
  riskLevel: RiskLevel
  recommendations: string[]
  groups: Record<string, number> // e.g., { partA, partB } or AQ categories
}

export function scoreAssessment(
  kind: AssessmentId,
  answers: Answer[],
): GenericResult {
  const cfg = CONFIGS[kind]

  const groups: Record<string, number> = {}
  if (cfg.groups) for (const g of Object.keys(cfg.groups)) groups[g] = 0

  let total = 0

  for (const a of answers) {
    const item = cfg.itemScore(a.value ?? 0, a.questionId)
    total += item

    if (cfg.groups) {
      for (const [name, ids] of Object.entries(cfg.groups)) {
        if (ids.includes(a.questionId)) groups[name] += item
      }
    }
  }

  const maxScore = cfg.numQuestions * cfg.maxPerQuestion
  const pct = maxScore ? (total / maxScore) * 100 : undefined

  const hit = cfg.thresholds.find((t) => t.when({ total, pct, groups }))!
  return {
    totalScore: total,
    percentageScore: pct,
    riskLevel: hit.level,
    recommendations: hit.recommendations({ total, pct, groups }),
    groups,
  }
}

/* ── Thin wrappers so your components keep their existing types ─────────── */
export function calculateASRSResults(answers: Answer[]): AssessmentResult {
  const r = scoreAssessment('asrs', answers)
  return {
    partAScore: r.groups.partA ?? 0,
    partBScore: r.groups.partB ?? 0,
    totalScore: r.totalScore,
    percentageScore: r.percentageScore ?? 0,
    riskLevel: r.riskLevel,
    recommendations: r.recommendations,
  }
}

export function calculateAQResultsUnified(answers: Answer[]): AQResult {
  const r = scoreAssessment('aq', answers)
  return {
    totalScore: r.totalScore,
    percentageScore: r.percentageScore ?? (r.totalScore / 50) * 100,
    categoryScores: {
      social: r.groups.social ?? 0,
      communication: r.groups.communication ?? 0,
      imagination: r.groups.imagination ?? 0,
      attention: r.groups.attention ?? 0,
      routine: r.groups.routine ?? 0,
    },
    riskLevel: r.riskLevel,
    recommendations: r.recommendations,
  }
}

// Optional: export with the original names your components expect
export { calculateASRSResults as calculateResults }
export { calculateAQResultsUnified as calculateAQResults }
